import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import { TargetsPanel } from './Targets';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import VerifiedIcon from '@mui/icons-material/Verified';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { useTranslation, Trans } from 'react-i18next';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../i18n/format';
import { api, ApiError } from '../services/api';
import { competitorsApi } from '../services/competitors';
import { useDomainFavicon } from '../hooks/useDomainFavicon';
import { resolveEntitlement, GEO_GENERATE_MAX } from '@traaker/shared';
import type {
  Envergure,
  GeoStrategy as GeoStrategyType,
  GeoStrategyMeta,
  GuardrailViolation,
  TargetsMeta,
} from '@traaker/shared';

const GEO_STRATEGY_LIMITS = {
  strategicAngle: 600,
  queryStrategy: 400,
  targetQueries: { maxItems: 30, maxCharsPerItem: 200 },
  targetAuthoritySources: { maxItems: 20, maxCharsPerItem: 200 },
  contentPillars: { maxItems: 10, maxCharsPerItem: 120 },
  competitiveFocus: { maxItems: 15, maxCharsPerItem: 120 },
} as const;

const ENVERGURE_LABEL_KEYS: Record<Envergure, string> = {
  'hyper-local': 'brandStrategy.envergure.hyperLocal',
  'multi-local': 'brandStrategy.envergure.multiLocal',
  'mono-country': 'brandStrategy.envergure.monoCountry',
  'multi-country': 'brandStrategy.envergure.multiCountry',
  global: 'brandStrategy.envergure.global',
};

type StatusDescriptor = {
  labelKey: string;
  color: 'default' | 'warning' | 'success' | 'info';
  icon?: React.ReactElement;
};

const GEO_STATUS: Record<string, StatusDescriptor> = {
  none: { labelKey: 'geoStrategy.status.none', color: 'default' },
  pending: { labelKey: 'geoStrategy.status.pending', color: 'warning' },
  validated: { labelKey: 'geoStrategy.status.validated', color: 'success', icon: <VerifiedIcon fontSize="small" /> },
  stale: { labelKey: 'geoStrategy.status.stale', color: 'warning' },
};

const BRAND_STATUS: Record<string, StatusDescriptor> = {
  draft: { labelKey: 'geoStrategy.brandStatus.draft', color: 'default' },
  validated: { labelKey: 'geoStrategy.brandStatus.validated', color: 'success', icon: <VerifiedIcon fontSize="small" /> },
  stale: { labelKey: 'geoStrategy.brandStatus.stale', color: 'warning' },
};

export default function GeoStrategy() {
  const { t } = useTranslation();
  const { activeBrand, refreshBrands } = useBrand();
  const { user, tenant } = useAuth();
  const canManage =
    user?.role === 'owner' || user?.role === 'admin' || user?.role === 'member';

  // competitiveFocus (competitors named in the GEO strategy) shares the
  // `competitors` tier quota — cap = min(structural maxItems, tier limit). The
  // server enforces the same on PATCH /geo-strategy.
  const competitorTierLimit = tenant
    ? resolveEntitlement('competitors', tenant.tier, {
        override: tenant.settings?.quotaOverrides?.competitors,
        packs: tenant.settings?.activePacks?.competitors,
      }).limit
    : -1;
  const competitiveFocusCap =
    competitorTierLimit === -1
      ? GEO_STRATEGY_LIMITS.competitiveFocus.maxItems
      : Math.min(GEO_STRATEGY_LIMITS.competitiveFocus.maxItems, competitorTierLimit);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [expandWhatsIt, setExpandWhatsIt] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [targetEnvergure, setTargetEnvergure] = useState<Envergure | ''>('');
  const [strategicAngle, setStrategicAngle] = useState('');
  const [queryStrategy, setQueryStrategy] = useState('');
  const [targetQueries, setTargetQueries] = useState<string[]>([]);
  const [targetAuthoritySources, setTargetAuthoritySources] = useState<string[]>([]);
  const [contentPillars, setContentPillars] = useState<string[]>([]);
  const [competitiveFocus, setCompetitiveFocus] = useState<string[]>([]);

  const [queryInput, setQueryInput] = useState('');
  const [sourceInput, setSourceInput] = useState('');
  const [pillarInput, setPillarInput] = useState('');
  const [focusInput, setFocusInput] = useState('');

  // Resolved domain per authority-source name (for favicons) — best-effort,
  // keyed by name, display-only (never persisted). The GEO generator emits
  // names ("Le Monde") not domains, so we resolve each via the same
  // Serper-backed resolver as onboarding to show its logo.
  const [sourceDomains, setSourceDomains] = useState<Record<string, string>>({});

  const loadFromBrand = useCallback(() => {
    if (!activeBrand) return;
    const g = activeBrand.geoStrategy;
    setTargetEnvergure(g?.targetEnvergure || '');
    setStrategicAngle(g?.strategicAngle || '');
    setQueryStrategy(g?.queryStrategy || '');
    setTargetQueries(g?.targetQueries || []);
    setTargetAuthoritySources(g?.targetAuthoritySources || []);
    setContentPillars(g?.contentPillars || []);
    setCompetitiveFocus(g?.competitiveFocus || []);
  }, [activeBrand]);

  useEffect(() => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setInfo(null);
    setFieldErrors({});
  }, [loadFromBrand]);

  // Resolve authority-source names → domains (for favicons). Direct-domain
  // entries resolve locally; the rest go through the Serper-backed resolver
  // (server-cached). Best-effort: a miss just leaves the letter-avatar fallback.
  useEffect(() => {
    if (!activeBrand) return;
    const sources = activeBrand.geoStrategy?.targetAuthoritySources || [];
    let cancelled = false;
    sources.forEach((name) => {
      if (sourceDomains[name]) return;
      const direct = domainIfLooksLikeOne(name);
      if (direct) {
        setSourceDomains((prev) => (prev[name] ? prev : { ...prev, [name]: direct }));
        return;
      }
      competitorsApi
        .resolveDomain(activeBrand.brandId, name)
        .then((res) => {
          if (cancelled || !res.domain) return;
          setSourceDomains((prev) => (prev[name] ? prev : { ...prev, [name]: res.domain! }));
        })
        .catch(() => void 0);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand]);

  const brandMeta = activeBrand?.strategyMeta;
  const geoMeta: GeoStrategyMeta = activeBrand?.geoStrategyMeta || { status: 'none' };
  const brandValidated = brandMeta?.status === 'validated';
  const hasGeo = activeBrand?.geoStrategy && Object.keys(activeBrand.geoStrategy).length > 0;

  const handleCancel = () => {
    loadFromBrand();
    setEditing(false);
    setError(null);
    setFieldErrors({});
  };

  const handleSave = async () => {
    if (!activeBrand) return;
    setSaving(true);
    setError(null);
    setFieldErrors({});

    const payload: Partial<GeoStrategyType> = {
      targetEnvergure: targetEnvergure || undefined,
      strategicAngle: strategicAngle.trim() || undefined,
      queryStrategy: queryStrategy.trim() || undefined,
      targetQueries: targetQueries.length > 0 ? targetQueries : undefined,
      targetAuthoritySources:
        targetAuthoritySources.length > 0 ? targetAuthoritySources : undefined,
      contentPillars: contentPillars.length > 0 ? contentPillars : undefined,
      competitiveFocus: competitiveFocus.length > 0 ? competitiveFocus : undefined,
    };

    try {
      await api.patch(`/brands/${activeBrand.brandId}/geo-strategy`, payload);
      await refreshBrands();
      setEditing(false);
      setInfo(t('geoStrategy.info.saved'));
    } catch (err: any) {
      if (err instanceof ApiError && err.details) {
        const violations = err.details as GuardrailViolation[];
        const errs: Record<string, string> = {};
        violations.forEach((v) => {
          const field = v.field.replace(/\[\d+\]$/, '');
          errs[field] = v.message;
        });
        setFieldErrors(errs);
        setError(t('geoStrategy.error.contentNotAllowed'));
      } else {
        setError(err.message || t('geoStrategy.error.save'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!activeBrand) return;
    setGenerating(true);
    setError(null);
    setInfo(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/geo-strategy/generate`, {});
      await refreshBrands();
      setInfo(t('geoStrategy.info.generated'));
    } catch (err: any) {
      setError(err.message || t('geoStrategy.error.generate'));
    } finally {
      setGenerating(false);
    }
  };

  const handleValidate = async () => {
    if (!activeBrand) return;
    setValidating(true);
    setError(null);
    try {
      await api.post(`/brands/${activeBrand.brandId}/geo-strategy/validate`, {});
      await refreshBrands();
      setInfo(t('geoStrategy.info.validated'));
    } catch (err: any) {
      setError(err.message || t('geoStrategy.error.validate'));
    } finally {
      setValidating(false);
    }
  };

  const chipHelper = (
    list: string[],
    cfg: { maxItems: number; maxCharsPerItem: number },
    fieldError?: string,
  ) =>
    fieldError ||
    t('geoStrategy.chipHelper', {
      count: list.length,
      max: cfg.maxItems,
      chars: cfg.maxCharsPerItem,
    });

  const handleChipAdd = (
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg: { maxItems: number; maxCharsPerItem: number },
  ) => {
    const trimmed = value.trim();
    if (list.length >= cfg.maxItems) {
      setInput('');
      return;
    }
    if (trimmed.length > cfg.maxCharsPerItem) return;
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setInput('');
  };

  const handleChipKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    value: string,
    list: string[],
    setList: (v: string[]) => void,
    setInput: (v: string) => void,
    cfg: { maxItems: number; maxCharsPerItem: number },
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChipAdd(value, list, setList, setInput, cfg);
    }
  };

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
          {t('geoStrategy.title')}
        </Typography>
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('geoStrategy.noBrand')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const statusDesc = GEO_STATUS[geoMeta.status] || GEO_STATUS.none;
  const brandStatusDesc = brandMeta ? BRAND_STATUS[brandMeta.status] : null;

  const metaBits: string[] = [];
  if (geoMeta.generatedAt) {
    metaBits.push(t('geoStrategy.meta.generated', { date: formatDate(geoMeta.generatedAt, { day: '2-digit', month: 'short', year: 'numeric' }) }));
  }
  if (geoMeta.validatedAt) {
    metaBits.push(t('geoStrategy.meta.validated', { date: formatDate(geoMeta.validatedAt, { day: '2-digit', month: 'short', year: 'numeric' }) }));
  }
  if (typeof geoMeta.generateCount === 'number' && geoMeta.generateCount > 0) {
    metaBits.push(t('geoStrategy.meta.generations', { count: geoMeta.generateCount, max: GEO_GENERATE_MAX }));
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          overflow: 'hidden',
          background: (theme) =>
            `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
              theme.palette.primary.light,
              0.03,
            )} 100%)`,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        }}
      >
        <CardContent
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <TravelExploreOutlinedIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {t('geoStrategy.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('geoStrategy.subtitle', { name: activeBrand.name })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }} data-tour="geo-status">
                <Chip
                  size="small"
                  label={t('geoStrategy.geoStatusChip', { status: t(statusDesc.labelKey) })}
                  color={statusDesc.color}
                  icon={statusDesc.icon}
                  variant="outlined"
                  sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
                />
                {brandStatusDesc && (
                  <Chip
                    size="small"
                    label={t('geoStrategy.brandStatusChip', { status: t(brandStatusDesc.labelKey) })}
                    color={brandStatusDesc.color}
                    icon={brandStatusDesc.icon}
                    variant="outlined"
                    sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
                  />
                )}
              </Box>
              {metaBits.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {metaBits.join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }} data-tour="geo-actions">
          {canManage && !editing && (
            <>
              {!hasGeo && (
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerate}
                  disabled={!brandValidated || generating}
                >
                  {generating ? t('geoStrategy.action.generating') : t('geoStrategy.action.generateAi')}
                </Button>
              )}
              {hasGeo && (
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerate}
                  disabled={!brandValidated || generating}
                >
                  {generating ? t('geoStrategy.action.generating') : t('geoStrategy.action.regenerate')}
                </Button>
              )}
              {hasGeo && (
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                  {t('geoStrategy.action.edit')}
                </Button>
              )}
              {hasGeo && geoMeta.status !== 'validated' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleValidate}
                  disabled={!brandValidated || validating}
                >
                  {validating ? t('geoStrategy.action.validating') : t('geoStrategy.action.validate')}
                </Button>
              )}
            </>
          )}
          {canManage && editing && (
            <>
              <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancel}>
                {t('geoStrategy.action.cancel')}
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
                {t('geoStrategy.action.save')}
              </Button>
            </>
          )}
        </Box>
        </CardContent>
      </Card>

      {/* What is GEO Strategy card */}
      <Card sx={{ mb: 3, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header with expand/collapse */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              bgcolor: expandWhatsIt ? 'background.paper' : 'background.default',
              borderBottom: expandWhatsIt ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
            onClick={() => setExpandWhatsIt(!expandWhatsIt)}
          >
            <Typography variant="h6" fontWeight={700}>
              {t('geoStrategy.whatIsIt.title')}
            </Typography>
            <IconButton
              size="small"
              sx={{
                transform: expandWhatsIt ? 'rotate(0deg)' : 'rotate(-180deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>

          {/* Expandable content */}
          <Collapse in={expandWhatsIt} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 2, alignItems: 'stretch' }}>
              {/* Left: Image (fills column height, no extra card growth) */}
              <Box sx={{ bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, borderRadius: 1, alignSelf: 'stretch' }}>
                <img
                  src="/assets/how-it-works/geo-strategy.webp"
                  alt="GEO Strategy"
                  style={{ maxWidth: '100%', width: 'auto', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              </Box>

              {/* Right: Text */}
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                    {t('geoStrategy.whatIsIt.subtitle')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t('geoStrategy.whatIsIt.intro')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    {t('geoStrategy.whatIsIt.whyIntent')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t('geoStrategy.whatIsIt.intentDiff')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    {t('geoStrategy.whatIsIt.opportunity')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t('geoStrategy.whatIsIt.opportunityText')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    {t('geoStrategy.whatIsIt.balance')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {t('geoStrategy.whatIsIt.balanceText')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                    {t('geoStrategy.whatIsIt.usage')}
                  </Typography>

                  <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {t('geoStrategy.whatIsIt.usageText')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {!brandValidated && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Trans i18nKey="geoStrategy.alert.brandNotValidated" components={{ strong: <strong /> }} />
        </Alert>
      )}

      {brandValidated && !hasGeo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('geoStrategy.alert.noGeo')}{' '}
          {canManage ? t('geoStrategy.alert.noGeoAdmin') : t('geoStrategy.alert.noGeoNonAdmin')}
        </Alert>
      )}

      {geoMeta.status === 'stale' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('geoStrategy.alert.stale')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Target envergure + strategic angle */}
        <Grid size={12}>
          <SectionCard
            icon={<ExploreOutlinedIcon />}
            title={t('geoStrategy.section.positioning')}
            usedBy={t('geoStrategy.usedBy.bpEmAngle')}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FieldLabel label={t('geoStrategy.field.targetEnvergure')} />
                {editing ? (
                  <Select
                    fullWidth
                    size="small"
                    value={targetEnvergure}
                    onChange={(e) => setTargetEnvergure(e.target.value as Envergure | '')}
                    displayEmpty
                  >
                    <MenuItem value="">{t('brandStrategy.envergure.undefined')}</MenuItem>
                    {Object.entries(ENVERGURE_LABEL_KEYS).map(([val, labelKey]) => (
                      <MenuItem key={val} value={val}>
                        {t(labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Typography>{targetEnvergure ? t(ENVERGURE_LABEL_KEYS[targetEnvergure]) : '—'}</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <FieldLabel label={t('geoStrategy.field.strategicAngle')} />
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    value={strategicAngle}
                    onChange={(e) =>
                      setStrategicAngle(e.target.value.slice(0, GEO_STRATEGY_LIMITS.strategicAngle))
                    }
                    placeholder={t('geoStrategy.placeholder.strategicAngle')}
                    error={!!fieldErrors.strategicAngle}
                    inputProps={{ maxLength: GEO_STRATEGY_LIMITS.strategicAngle }}
                    helperText={
                      fieldErrors.strategicAngle ||
                      `${strategicAngle.length} / ${GEO_STRATEGY_LIMITS.strategicAngle}`
                    }
                  />
                ) : (
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{strategicAngle || '—'}</Typography>
                )}
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>

        {/* ── Cibles — expandable (D27) ── */}
        <Grid size={12}>
          <CiblesAccordion targetsMeta={activeBrand?.targetsMeta} />
        </Grid>

        {/* Query Strategy */}
        <Grid size={12}>
          <SectionCard icon={<PsychologyOutlinedIcon />} title={t('geoStrategy.section.queryStrategy')} usedBy={t('geoStrategy.usedBy.bpPromptGuide')}>
            <FieldLabel label={t('geoStrategy.field.queryStrategy')} />
            {editing ? (
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                value={queryStrategy}
                onChange={(e) =>
                  setQueryStrategy(e.target.value.slice(0, GEO_STRATEGY_LIMITS.queryStrategy))
                }
                placeholder={t('geoStrategy.placeholder.queryStrategy')}
                error={!!fieldErrors.queryStrategy}
                inputProps={{ maxLength: GEO_STRATEGY_LIMITS.queryStrategy }}
                helperText={
                  fieldErrors.queryStrategy ||
                  `${queryStrategy.length} / ${GEO_STRATEGY_LIMITS.queryStrategy}`
                }
              />
            ) : (
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{queryStrategy || '—'}</Typography>
            )}
          </SectionCard>
        </Grid>

        {/* Authority sources */}
        <Grid size={12}>
          <SectionCard
            icon={<NewspaperOutlinedIcon />}
            title={t('geoStrategy.section.authoritySources')}
            usedBy={t('geoStrategy.usedBy.emAllowlist')}
          >
            <FieldLabel label={t('geoStrategy.field.authoritySources')} />
            {editing ? (
              <Box>
                <ChipList
                  items={targetAuthoritySources}
                  onDelete={(i) =>
                    setTargetAuthoritySources(targetAuthoritySources.filter((_, idx) => idx !== i))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  value={sourceInput}
                  onChange={(e) =>
                    setSourceInput(
                      e.target.value.slice(
                        0,
                        GEO_STRATEGY_LIMITS.targetAuthoritySources.maxCharsPerItem,
                      ),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      sourceInput,
                      targetAuthoritySources,
                      setTargetAuthoritySources,
                      setSourceInput,
                      GEO_STRATEGY_LIMITS.targetAuthoritySources,
                    )
                  }
                  placeholder={t('geoStrategy.placeholder.authoritySources')}
                  inputProps={{
                    maxLength: GEO_STRATEGY_LIMITS.targetAuthoritySources.maxCharsPerItem,
                  }}
                  helperText={chipHelper(
                    targetAuthoritySources,
                    GEO_STRATEGY_LIMITS.targetAuthoritySources,
                  )}
                  disabled={
                    targetAuthoritySources.length >=
                    GEO_STRATEGY_LIMITS.targetAuthoritySources.maxItems
                  }
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <SourceFaviconList items={targetAuthoritySources} domains={sourceDomains} />
            )}
          </SectionCard>
        </Grid>

        {/* Content pillars */}
        <Grid size={12}>
          <SectionCard
            icon={<ViewColumnOutlinedIcon />}
            title={t('geoStrategy.section.contentPillars')}
            usedBy={t('geoStrategy.usedBy.contentQualityVeille')}
          >
            <FieldLabel label={t('geoStrategy.field.contentPillars')} />
            {editing ? (
              <Box>
                <ChipList
                  items={contentPillars}
                  onDelete={(i) => setContentPillars(contentPillars.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={pillarInput}
                  onChange={(e) =>
                    setPillarInput(
                      e.target.value.slice(0, GEO_STRATEGY_LIMITS.contentPillars.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      pillarInput,
                      contentPillars,
                      setContentPillars,
                      setPillarInput,
                      GEO_STRATEGY_LIMITS.contentPillars,
                    )
                  }
                  placeholder={t('geoStrategy.placeholder.contentPillars')}
                  error={!!fieldErrors.contentPillars}
                  inputProps={{ maxLength: GEO_STRATEGY_LIMITS.contentPillars.maxCharsPerItem }}
                  helperText={chipHelper(
                    contentPillars,
                    GEO_STRATEGY_LIMITS.contentPillars,
                    fieldErrors.contentPillars,
                  )}
                  disabled={contentPillars.length >= GEO_STRATEGY_LIMITS.contentPillars.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={contentPillars} />
            )}
          </SectionCard>
        </Grid>

        {/* Competitive focus */}
        <Grid size={12}>
          <SectionCard
            icon={<EmojiEventsOutlinedIcon />}
            title={t('geoStrategy.section.competitiveFocus')}
            usedBy={t('geoStrategy.usedBy.bpComparative')}
          >
            <FieldLabel
              label={t('geoStrategy.field.competitiveFocus', {
                count: competitiveFocus.length,
                max: competitiveFocusCap,
              })}
            />
            {editing ? (
              <Box>
                <ChipList
                  items={competitiveFocus}
                  onDelete={(i) =>
                    setCompetitiveFocus(competitiveFocus.filter((_, idx) => idx !== i))
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  value={focusInput}
                  onChange={(e) =>
                    setFocusInput(
                      e.target.value.slice(
                        0,
                        GEO_STRATEGY_LIMITS.competitiveFocus.maxCharsPerItem,
                      ),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      focusInput,
                      competitiveFocus,
                      setCompetitiveFocus,
                      setFocusInput,
                      // Effective cap = min(structural, tier competitors quota).
                      { ...GEO_STRATEGY_LIMITS.competitiveFocus, maxItems: competitiveFocusCap },
                    )
                  }
                  placeholder={t('geoStrategy.placeholder.competitiveFocus')}
                  inputProps={{ maxLength: GEO_STRATEGY_LIMITS.competitiveFocus.maxCharsPerItem }}
                  helperText={
                    competitiveFocus.length >= competitiveFocusCap
                      ? t('geoStrategy.competitiveFocusLimit', { max: competitiveFocusCap })
                      : chipHelper(competitiveFocus, {
                          ...GEO_STRATEGY_LIMITS.competitiveFocus,
                          maxItems: competitiveFocusCap,
                        })
                  }
                  disabled={competitiveFocus.length >= competitiveFocusCap}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <ChipList items={competitiveFocus} />
            )}
          </SectionCard>
        </Grid>

        {/* Requêtes cibles — at the bottom */}
        <Grid size={12}>
          <SectionCard
            icon={<ManageSearchOutlinedIcon />}
            title={t('geoStrategy.section.targetQueries')}
            usedBy={t('geoStrategy.usedBy.bpMatrix')}
          >
            <FieldLabel label={t('geoStrategy.field.targetQueries')} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('geoStrategy.hint.targetQueries')}
            </Typography>
            {editing ? (
              <Box>
                <QueryList
                  items={targetQueries}
                  onDelete={(i) => setTargetQueries(targetQueries.filter((_, idx) => idx !== i))}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={queryInput}
                  onChange={(e) =>
                    setQueryInput(
                      e.target.value.slice(0, GEO_STRATEGY_LIMITS.targetQueries.maxCharsPerItem),
                    )
                  }
                  onKeyDown={(e) =>
                    handleChipKeyDown(
                      e,
                      queryInput,
                      targetQueries,
                      setTargetQueries,
                      setQueryInput,
                      GEO_STRATEGY_LIMITS.targetQueries,
                    )
                  }
                  placeholder={t('geoStrategy.placeholder.targetQueries')}
                  error={!!fieldErrors.targetQueries}
                  inputProps={{ maxLength: GEO_STRATEGY_LIMITS.targetQueries.maxCharsPerItem }}
                  helperText={chipHelper(
                    targetQueries,
                    GEO_STRATEGY_LIMITS.targetQueries,
                    fieldErrors.targetQueries,
                  )}
                  disabled={targetQueries.length >= GEO_STRATEGY_LIMITS.targetQueries.maxItems}
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <QueryList items={targetQueries} />
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {(saving || generating) && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: '#070B14CC',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.primary">
            {generating ? t('geoStrategy.overlay.generating') : t('geoStrategy.overlay.saving')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ── Cibles accordion (D27) ──

/**
 * Flat card embedding TargetsPanel — same visual level as the other strategy
 * cards.  Editing targets MUST NOT flip geoStrategyMeta to stale (D17).
 */
function CiblesAccordion({ targetsMeta: _targetsMeta }: { targetsMeta?: TargetsMeta }) {
  return (
    <Card
      sx={{
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <TargetsPanel />
    </Card>
  );
}

// ── Helpers ──

/** Return the bare host if `s` already looks like a URL/domain, else null. */
function domainIfLooksLikeOne(s: string): string | null {
  const t = (s || '').trim().toLowerCase();
  if (/^https?:\/\//.test(t)) {
    try {
      return new URL(t).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }
  if (!t.includes(' ') && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(t)) {
    return t.replace(/^www\./, '');
  }
  return null;
}

// ── Helper components ──

function SectionCard({
  icon,
  title,
  usedBy,
  children,
}: {
  icon?: React.ReactNode;
  title: string;
  usedBy?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card
      sx={{
        height: '100%',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.24),
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            mb: 2.5,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          {icon && (
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '& svg': { fontSize: 20 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
            {usedBy && (
              <Typography variant="caption" color="text.secondary">
                {t('geoStrategy.usedByLabel', { value: usedBy })}
              </Typography>
            )}
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function FieldLabel({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        mb: 0.75,
        display: 'block',
        color: 'text.secondary',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </Typography>
  );
}

function ChipList({
  items,
  onDelete,
}: {
  items: string[];
  onDelete?: (index: number) => void;
}) {
  if (items.length === 0 && !onDelete) {
    return (
      <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {items.map((item, i) => (
        <Chip
          key={`${item}-${i}`}
          label={item}
          size="small"
          onDelete={onDelete ? () => onDelete(i) : undefined}
          sx={{
            fontWeight: 500,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            color: 'primary.dark',
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
            '& .MuiChip-deleteIcon': {
              color: (theme) => alpha(theme.palette.primary.main, 0.5),
              '&:hover': { color: 'primary.main' },
            },
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Sentence-length example prompts read poorly as chips. Render them as a clean
 * numbered list (a lightweight "table" of one prompt per row) — legible in view
 * mode, deletable in edit mode.
 */
function QueryList({
  items,
  onDelete,
}: {
  items: string[];
  onDelete?: (index: number) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
        {t('geoStrategy.emptyQueries')}
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {items.map((item, i) => (
        <Box
          key={`${item}-${i}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderBottom: i < items.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            bgcolor: i % 2 === 1 ? (theme) => alpha(theme.palette.primary.main, 0.02) : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05) },
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            {i + 1}
          </Box>
          <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
            {item}
          </Typography>
          {onDelete && (
            <CloseIcon
              fontSize="small"
              onClick={() => onDelete(i)}
              sx={{
                cursor: 'pointer',
                color: 'text.disabled',
                fontSize: 18,
                '&:hover': { color: 'error.main' },
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Authority sources rendered as favicon chips (view mode). Domains are resolved
 * upstream (name → domain via Serper) and passed in; a miss falls back to a
 * letter avatar. Display-only — the persisted value stays the source names.
 */
function SourceFaviconList({
  items,
  domains,
}: {
  items: string[];
  domains: Record<string, string>;
}) {
  if (items.length === 0) {
    return (
      <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items.map((name, i) => (
        <SourceFaviconChip key={`${name}-${i}`} name={name} domain={domains[name] || null} />
      ))}
    </Box>
  );
}

function SourceFaviconChip({ name, domain }: { name: string; domain: string | null }) {
  const favicon = useDomainFavicon(domain || undefined, 64);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 0.75,
        pr: 1.5,
        py: 0.5,
        borderRadius: 5,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.16),
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
      }}
    >
      <Avatar
        src={favicon || undefined}
        sx={{ width: 22, height: 22, fontSize: 11, bgcolor: 'action.hover', color: 'text.secondary' }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>
      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.dark' }}>
        {name}
      </Typography>
    </Box>
  );
}
